import { AlertTriangle, Loader2, Wrench } from 'lucide-react';
import { Checkbox } from '@/components/atoms/checkbox';
import { Input } from '@/components/atoms/input';
import { Textarea } from '@/components/atoms/textarea';
import { useState } from 'react';
import { useAllEquipment, useEquipment } from '@/hooks/equipment/useEquipment';
import { cn } from '@/lib/utils';
import type { Equipment } from '@/types/equipment';

/** Make and model, for the line under the unit's name. */
function unitSpec(equipment: Equipment) {
  return [equipment.make, equipment.model].filter(Boolean).join(' ');
}

function canAssociate(equipment: Equipment) {
  return equipment.status === 'ACTIVE' || equipment.status === 'UNKNOWN';
}

export function EquipmentLineAssociation({
  customerId,
  serviceAddressId,
  equipmentIds,
  locationOverrideReason,
  onChange,
  title = 'Existing equipment serviced',
  ownerScopedOnly = false,
  hideCrossCustomerSearch = ownerScopedOnly,
  requireCompleteSelection = false,
  bare = false,
}: {
  customerId?: string | null;
  serviceAddressId?: string | null;
  equipmentIds: string[];
  locationOverrideReason?: string;
  onChange: (
    equipmentIds: string[],
    locationOverrideReason?: string,
    selectedEquipment?: Equipment[],
  ) => void;
  /** Heading — the picker is reused for lines, whole jobs, and estimates. */
  title?: string;
  /** Drop the disclosure and its frame; the caller supplies the heading. */
  bare?: boolean;
  /**
   * Job creation only links units the job's own contact owns: the server drops
   * everything else and CreateJobDto carries no override reason. Hide both
   * affordances there so the form can't promise a link that never happens.
   */
  ownerScopedOnly?: boolean;
  /** Hide cross-customer search while still allowing audited selections at another address. */
  hideCrossCustomerSearch?: boolean;
  /** Load every customer unit so callers that reprice from this selection never price a partial page. */
  requireCompleteSelection?: boolean;
}) {
  const [overrideSearch, setOverrideSearch] = useState('');
  const pagedCustomerQuery = useEquipment(
    { customerId: customerId ?? undefined, limit: 100 },
    Boolean(customerId) && !requireCompleteSelection,
  );
  const completeCustomerQuery = useAllEquipment(
    { customerId: customerId ?? undefined },
    Boolean(customerId) && requireCompleteSelection,
  );
  const customerQuery = requireCompleteSelection ? completeCustomerQuery : pagedCustomerQuery;
  const overrideQuery = useEquipment(
    { q: overrideSearch.trim() || undefined, limit: 100 },
    Boolean(customerId) && !hideCrossCustomerSearch,
  );
  const customerUnits = customerQuery.data?.items ?? [];
  const units = customerUnits.filter(canAssociate);
  const exact = serviceAddressId
    ? units.filter((unit) => unit.serviceAddressId === serviceAddressId)
    : [];
  const otherLocations = serviceAddressId
    ? units.filter((unit) => unit.serviceAddressId !== serviceAddressId)
    : units;
  const otherCustomers = (overrideQuery.data?.items ?? [])
    .filter(canAssociate)
    .filter((unit) => unit.customerId !== customerId);
  const exactIds = new Set(
    customerUnits
      .filter((unit) => unit.serviceAddressId === serviceAddressId)
      .map((unit) => unit.id),
  );
  const hasOverrideSelection = equipmentIds.some((id) => !exactIds.has(id));

  function toggle(id: string, checked: boolean) {
    const next = checked
      ? Array.from(new Set([...equipmentIds, id]))
      : equipmentIds.filter((candidate) => candidate !== id);
    const keepsOverride = next.some((candidate) => !exactIds.has(candidate));
    // Keep already-selected inactive units in the pricing input even though
    // they are not offered for a new association. Dropping them here lowers a
    // membership's price while leaving their ids attached to the line.
    const knownUnits = [...customerUnits, ...otherCustomers];
    onChange(
      next,
      keepsOverride ? locationOverrideReason : undefined,
      knownUnits.filter((unit) => next.includes(unit.id)),
    );
  }

  /**
   * One line per unit: tick, name, make and model, and the asset number on the
   * right. Stacked over three lines this was a 66px card each, so six units
   * filled a screen — and the make/model and number are things you scan across,
   * not read down. The serial lives in the tooltip; it is a lookup key, not
   * something anyone reads while picking.
   */
  function renderUnit(unit: Equipment, isOverride = false) {
    const selected = equipmentIds.includes(unit.id);
    const spec = unitSpec(unit);
    return (
      <label
        key={unit.id}
        title={unit.serialNumber ? `Serial ${unit.serialNumber}` : undefined}
        className={cn(
          'flex h-9 cursor-pointer items-center gap-2.5 px-3 transition-colors',
          selected ? 'bg-muted' : 'hover:bg-muted/50',
        )}
      >
        <Checkbox
          className="shrink-0"
          checked={selected}
          onCheckedChange={(checked) => toggle(unit.id, checked === true)}
        />
        <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-foreground">
          {unit.name}
          {spec ? <span className="ml-1.5 font-normal text-muted-foreground">{spec}</span> : null}
        </span>
        {isOverride ? (
          <span className="shrink-0 rounded bg-muted px-1.5 py-px text-[10px] font-medium text-muted-foreground">
            {unit.customerId === customerId ? 'Other address' : 'Other customer'}
          </span>
        ) : null}
        <span className="shrink-0 text-[10.5px] tabular-nums text-muted-foreground/70">
          {unit.equipmentNumber}
        </span>
      </label>
    );
  }

  /**
   * A group of units, ruled into one panel rather than laid out as free-floating
   * cards. Three bordered boxes per row left the last row of every group ragged,
   * and a name wide enough to truncate at a third of the width had the rest of
   * the row empty beside it.
   */
  function unitGroup(text: string, group: Equipment[], isOverride = false) {
    return (
      <div key={text}>
        <p className="border-b bg-muted/25 px-3 py-1.5 text-[11px] font-medium text-muted-foreground">
          {text}
        </p>
        <div className="grid sm:grid-cols-2 [&>*]:border-b [&>*]:border-border/60 sm:[&>*:nth-child(odd)]:border-r sm:[&>*:last-child:nth-child(odd)]:border-r-0">
          {group.map((unit) => renderUnit(unit, isOverride))}
        </div>
      </div>
    );
  }

  // Equipment association is optional/secondary on most lines, so the whole
  // block folds behind a one-line summary. It opens automatically once a unit is
  // linked so the selection is never hidden.
  const hasSelection = equipmentIds.length > 0;

  const body = (
    <div className="space-y-3">
      {!customerId ? (
        <p className="text-xs leading-6 text-muted-foreground">
          Select a customer to see the equipment registered at their addresses.
        </p>
      ) : customerQuery.isLoading ? (
        <p className="flex items-center gap-2 text-xs leading-6 text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> Loading equipment…
        </p>
      ) : customerQuery.isError ? (
        <p className="text-xs leading-6 text-destructive">Equipment could not be loaded.</p>
      ) : (
        <>
          {/* Equipment is registered against one service address, so the units
              already at THIS address are the answer in the common case and
              lead the list. Everything below them is a deliberate exception. */}
          {!serviceAddressId ? (
            <p className="text-xs leading-6 text-muted-foreground">
              Choose a service address to see the equipment registered there.
            </p>
          ) : exact.length === 0 && otherLocations.length === 0 ? (
            <p className="text-xs leading-6 text-muted-foreground">
              Nothing registered at this address yet.
            </p>
          ) : (
            /* Both groups live in one panel: they are one list of the same
               customer's units, split by how likely each is to be the right
               one, so a rule between them says more than a gap does. The last
               rule tucks under the panel border instead of doubling it. */
            <div className="overflow-hidden rounded-lg border bg-card [&>*:last-child]:-mb-px">
              {exact.length > 0 ? unitGroup(`At this address · ${exact.length}`, exact) : null}
              {exact.length === 0 ? (
                <p className="border-b bg-muted/25 px-3 py-1.5 text-[11px] text-muted-foreground">
                  Nothing registered at this address — this customer&apos;s other addresses are
                  below.
                </p>
              ) : null}
              {otherLocations.length > 0
                ? unitGroup(`Other addresses · ${otherLocations.length}`, otherLocations, true)
                : null}
            </div>
          )}
          {/* Audited override is inline (no second collapse) so the whole card
              opens in one click. Results stay hidden until the operator searches,
              so we never dump another customer's equipment by default. Owner-scoped
              lines (main) never expose another customer's equipment at all. */}
          {hideCrossCustomerSearch ? null : (
            <div className="space-y-2">
              <p className="text-[11px] font-medium text-muted-foreground">
                Another customer&apos;s equipment · audited override
              </p>
              <Input
                value={overrideSearch}
                onChange={(event) => setOverrideSearch(event.target.value)}
                placeholder="Search number, name, make, model, or serial…"
                className="h-8 text-xs"
              />
              {overrideSearch.trim().length === 0 ? null : overrideQuery.isLoading ? (
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="size-3.5 animate-spin" /> Searching…
                </p>
              ) : otherCustomers.length > 0 ? (
                <div className="overflow-hidden rounded-lg border bg-card [&>*:last-child]:-mb-px">
                  {unitGroup(`Matches · ${otherCustomers.length}`, otherCustomers, true)}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No matching equipment.</p>
              )}
            </div>
          )}
          {hasOverrideSelection && !ownerScopedOnly ? (
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs font-medium">
                <AlertTriangle className="size-3.5 text-warning" /> Location override reason
              </label>
              <Textarea
                rows={2}
                value={locationOverrideReason ?? ''}
                onChange={(event) =>
                  onChange(
                    equipmentIds,
                    event.target.value,
                    [...customerUnits, ...otherCustomers].filter((unit) =>
                      equipmentIds.includes(unit.id),
                    ),
                  )
                }
                placeholder="Explain why equipment from another location is related…"
                className="min-h-0 text-xs"
              />
              {(locationOverrideReason?.trim().length ?? 0) < 3 ? (
                <p className="text-[11px] text-warning">
                  A reason of at least 3 characters is required before saving.
                </p>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );

  // `bare`: the caller already titles the block, so the disclosure and its
  // dashed frame would be a box drawn inside a box saying the same thing twice.
  if (bare) return body;

  return (
    <details
      open={hasSelection}
      className="group rounded-md border border-dashed bg-background/70 [&_summary::-webkit-details-marker]:hidden"
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-xs font-semibold text-muted-foreground">
        <Wrench className="size-3.5" />
        <span className="text-foreground">{title}</span>
        {hasSelection ? (
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium">
            {equipmentIds.length}
          </span>
        ) : (
          <span className="ml-auto text-[11px] font-normal">Optional</span>
        )}
      </summary>
      <div className="px-3 pb-3">{body}</div>
    </details>
  );
}
